// <emma-chat-refine> — in-conversation refine input.
//
// Lift 1:1 from `design-system/components/refine-input.{css,html}`. Layout:
//
//   .refine-wrap (white, soft border, no shadow)
//     .refine-row
//       <textarea class="refine-input" rows="1"> (multi-line, auto-grows)
//       <button class="refine-btn">  (28×28 light-blue, arrow → check on submit)
//
// Modifiers (from DS):
//   - .refine-wrap--focused    static-focus mock
//   - .refine-wrap--submitted  transient feedback (~1.2s) — checkmark icon, dim
//   - .refine-wrap--dim        terminal state (after N refines / soft-gate)
//
// Events:
//   - emma-refine-submit { text } — bubbles + composed
//
// Keyboard: Enter submits, Shift+Enter inserts a newline (matches monolith
// behaviour for multi-line refine queries — "compare AWS Frankfurt to GCP
// Netherlands\nspot only\ncheaper" works as a single submit).
//
// Auto-grow: textarea height tracks scrollHeight on input — DS uses rows="1"
// + resize:none + overflow-y:hidden as the static baseline. Widget extends
// with capped auto-grow (max ~6 lines, then internal scroll). DS's
// refine-input.css/.html documents the multi-line variant via comments.

import { LitElement, css, html } from 'lit';
import { customElement, property, query, state } from 'lit/decorators.js';
import { designTokens } from './design-tokens.js';
import { dispatchStopThinking, stopIcon } from './stop-thinking.js';

@customElement('emma-chat-refine')
export class EmmaChatRefine extends LitElement {
  /** Disable submission while parent is processing. */
  @property({ type: Boolean })
  disabled = false;

  /** Soft-secondary appearance (post N refines / soft-gate). */
  @property({ type: Boolean })
  secondary = false;

  /** Transient post-submit feedback — caller sets true for ~1.2s after
   *  successful submit, then resets. Reflected so :host([submitted])
   *  selectors (e.g. show .refine-scanning indicator) work. */
  @property({ type: Boolean, reflect: true })
  submitted = false;

  /** Placeholder text. */
  @property({ type: String })
  placeholder = 'Refine: only AWS, in USD, in Frankfurt…';

  /** Live thinking-progress message shown in the .refine-scanning row
   *  below the input while `submitted=true`. Falls back to "Searching…"
   *  matching monolith refineThinking label (index.html :2278). */
  @property({ type: String, attribute: 'thinking-message' })
  thinkingMessage = '';

  @state() private text = '';

  @query('textarea') private input!: HTMLTextAreaElement;

  /** Auto-grow cap — beyond this many lines the textarea internal-scrolls
   *  instead of pushing the bubble taller. Mirrors monolith behaviour. */
  private static readonly MAX_AUTO_GROW_PX = 132; // ~6 lines × ~22px line-height

  static override styles = [
    designTokens,
    // Lifted 1:1 from design-system/components/refine-input.css.
    css`
      :host {
        display: block;
      }
      /* :host([hidden]) is explicit because :host display:block beats the
         global [hidden] user-agent rule. Without this, ?hidden binding
         from the parent would no-op. */
      :host([hidden]) {
        display: none;
      }
      /* Smooth reveal when [hidden] is removed (chip click / refine-trigger
         opens the input). The animation rule applies fresh each time the
         selector becomes matchable, so toggling hidden replays the entry. */
      :host(:not([hidden])) {
        animation: refine-input-arrive 320ms cubic-bezier(0, 0, 0.2, 1) both;
      }
      @keyframes refine-input-arrive {
        0% {
          opacity: 0;
          transform: translateY(8px);
        }
        100% {
          opacity: 1;
          transform: translateY(0);
        }
      }
      @media (prefers-reduced-motion: reduce) {
        :host(:not([hidden])) {
          animation: none;
        }
      }

      .refine-wrap {
        background: var(--white);
        border: 1px solid var(--gray-10);
        border-radius: var(--radius-md);
        padding: 12px 16px;
        transition: border-color var(--duration-fast) var(--easing);
      }
      .refine-wrap:focus-within,
      :host([focused]) .refine-wrap {
        border-color: var(--gray-20);
      }

      :host([submitted]) .refine-wrap {
        /* DS .refine-wrap--submitted */
      }
      /* During submitted state the .refine-scanning indicator below the
         row provides feedback (Braille + "Searching…" / live message),
         so the send button is redundant and visually orphaned (sits at
         the top-right of a tall wrap). Hide it; let the textarea claim
         full width while the search is in flight. Restored on completion
         when submitted flips back to false. */
      :host([submitted]) .refine-btn {
        display: none;
      }
      :host([submitted]) .refine-input {
        pointer-events: none;
      }

      :host([secondary]) .refine-wrap {
        border-color: var(--gray-07);
      }
      :host([secondary]) .refine-input {
        color: var(--gray-40);
      }
      :host([secondary]) .refine-input::placeholder {
        color: var(--gray-20);
      }
      :host([secondary]) .refine-btn {
        opacity: 0.4;
        pointer-events: none;
      }

      .refine-row {
        display: flex;
        align-items: flex-end;
        gap: var(--space-2);
      }

      .refine-input {
        flex: 1;
        border: none;
        outline: none;
        font-family: var(--font-sans);
        font-size: var(--text-sm);
        color: var(--color-text-muted);
        background: transparent;
        resize: none;
        /* Hidden until content overflows MAX_AUTO_GROW_PX cap. JS sets
           explicit height on input; once cap reached, scroll appears. */
        overflow-y: auto;
        line-height: var(--leading-md);
        min-width: 0;
        /* rows="1" gives initial height; auto-grow extends via JS. */
      }
      .refine-input::placeholder {
        color: var(--gray-40);
      }

      .refine-btn {
        width: 28px;
        height: 28px;
        border-radius: var(--radius-sm);
        background: var(--blue-15);
        border: none;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
        transition: background var(--duration-fast) var(--easing),
          transform var(--duration-fast) var(--easing);
      }
      .refine-btn svg {
        color: var(--blue-80);
        transition: transform var(--duration-fast) var(--easing);
      }
      @media (hover: hover) {
        .refine-btn:hover {
          background: var(--blue-30);
        }
        .refine-btn:hover svg {
          transform: translateX(2px);
        }
      }
      .refine-btn:active {
        transform: scale(0.95);
      }
      .refine-btn:disabled {
        opacity: 0.4;
        pointer-events: none;
        cursor: default;
      }

      /* Scanning indicator below the row — visible while submitted=true.
         Mirror monolith .emma-hero__refine-scanning (index.html :2276)
         + DS .result-intro-spinner (Braille track + animated cycle). */
      .refine-scanning {
        display: none;
        align-items: center;
        gap: 8px;
        margin-top: var(--space-2);
        font-family: var(--font-sans);
        font-size: var(--text-sm);
        line-height: var(--leading-none);
        color: var(--gray-60);
      }
      :host([submitted]) .refine-scanning {
        display: flex;
      }
      .refine-scanning-spinner {
        position: relative;
        display: inline-block;
        flex-shrink: 0;
        font-family: monospace;
        font-size: 1em;
        line-height: 1;
      }
      .refine-scanning-spinner::before {
        content: '⠿';
        color: var(--gray-15);
      }
      .refine-scanning-spinner::after {
        content: '⠁';
        position: absolute;
        left: 0;
        top: 0;
        color: var(--blue-60);
        animation: refine-braille-spin 0.9s infinite;
      }
      @keyframes refine-braille-spin {
        0% { content: '⠁'; }
        16.7% { content: '⠈'; }
        33.3% { content: '⠐'; }
        50% { content: '⠠'; }
        66.7% { content: '⠄'; }
        83.3% { content: '⠂'; }
        100% { content: '⠁'; }
      }

      /* Stop control at the right edge of the scanning row — cancels the
         in-flight refine (bubbles emma-stop-thinking to root <emma-chat>).
         The typed refine text stays in the textarea after the stop. */
      .thinking-stop {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        margin-left: auto;
        padding: 4px 10px;
        background: none;
        border: 1px solid var(--gray-10);
        border-radius: var(--radius-sm);
        color: var(--color-text-muted);
        font-family: var(--font-sans);
        font-size: var(--text-sm);
        font-weight: var(--font-medium);
        line-height: 1;
        cursor: pointer;
        transition:
          color var(--duration-fast) var(--easing),
          background var(--duration-fast) var(--easing);
      }
      .thinking-stop svg {
        flex: none;
      }
      @media (hover: hover) {
        .thinking-stop:hover {
          color: var(--color-text);
          background: var(--gray-10);
        }
      }

      @media (prefers-reduced-motion: reduce) {
        .refine-scanning-spinner::after {
          animation: none;
        }
        .refine-wrap,
        .refine-btn,
        .refine-btn svg {
          transition: none;
        }
        .refine-btn:active {
          transform: none;
        }
      }
    `,
  ];

  private handleInput = (e: Event): void => {
    const ta = e.target as HTMLTextAreaElement;
    this.text = ta.value;
    this.autoGrow(ta);
  };

  /** Reset height to auto first so shrinking works, then expand to scrollHeight
   *  capped at MAX_AUTO_GROW_PX. After the cap, internal scroll takes over. */
  private autoGrow(ta: HTMLTextAreaElement): void {
    ta.style.height = 'auto';
    const next = Math.min(ta.scrollHeight, EmmaChatRefine.MAX_AUTO_GROW_PX);
    ta.style.height = next + 'px';
  }

  private handleKeydown = (e: KeyboardEvent): void => {
    // Enter submits; Shift+Enter inserts a newline (browser default for
    // textarea — we just don't preventDefault in that case).
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      this.submit();
    }
  };

  private submit(): void {
    const trimmed = this.text.trim();
    if (!trimmed || this.disabled || this.secondary || this.submitted) return;
    this.dispatchEvent(
      new CustomEvent('emma-refine-submit', {
        detail: { text: trimmed },
        bubbles: true,
        composed: true,
      }),
    );
  }

  /** Stop button in the scanning row — cancel the in-flight refine.
   *  Bubbles emma-stop-thinking to root <emma-chat>; the parent flips
   *  `submitted` back to false via store.isSubmitting, restoring the
   *  input (text intact) for edit/resend. */
  private handleStopClick = (): void => {
    dispatchStopThinking(this);
  };

  /** Clear input — exposed so parent can reset after a successful turn. */
  clear(): void {
    this.text = '';
    if (this.input) {
      this.input.value = '';
      this.input.style.height = 'auto';
    }
  }

  /** Programmatic focus — used by parent (turn) to focus on trigger-open. */
  focusInput(): void {
    if (this.input) this.input.focus();
  }

  /** Programmatic value-set — used by quick-chip click which fills the input. */
  setText(value: string): void {
    this.text = value;
    if (this.input) {
      this.input.value = value;
      this.autoGrow(this.input);
    }
  }

  override render() {
    const canSubmit =
      this.text.trim().length > 0 && !this.disabled && !this.secondary && !this.submitted;
    const scanningLabel = this.thinkingMessage || 'Searching…';
    return html`
      <div class="refine-wrap">
        <div class="refine-row">
          <textarea
            class="refine-input"
            rows="1"
            aria-label="Refine search"
            ?disabled=${this.disabled || this.secondary}
            .value=${this.text}
            placeholder="${this.placeholder}"
            @input=${this.handleInput}
            @keydown=${this.handleKeydown}
          ></textarea>
          <button
            class="refine-btn"
            ?disabled=${!canSubmit}
            aria-label="Submit"
            @click=${this.submit}
          >
            ${this.submitted ? this.checkIcon() : this.arrowIcon()}
          </button>
        </div>
        <div class="refine-scanning" aria-live="polite">
          <span class="refine-scanning-spinner" aria-hidden="true"></span>
          <span class="refine-scanning-label">${scanningLabel}</span>
          <button
            type="button"
            class="thinking-stop"
            aria-label="Stop search"
            @click=${this.handleStopClick}
          >
            ${stopIcon()} Stop
          </button>
        </div>
      </div>
    `;
  }

  private arrowIcon() {
    return html`
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
        <path
          d="M13.5 8H2.5M8 3l5 5-5 5"
          stroke="currentColor"
          stroke-width="1.5"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>
    `;
  }

  private checkIcon() {
    return html`
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
        <path
          d="M2 8.5l3.5 3.5 8-8"
          stroke="currentColor"
          stroke-width="1.5"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'emma-chat-refine': EmmaChatRefine;
  }
}
